import { BiTag } from "react-icons/bi";
import { productStatusStyles } from "@/common/utils/general";
import { LoadingSkeleton } from "@/common/components/skeleton";
import { StockStatus } from "@/common/types/others";

type ProductCardProps = {
  name: string;
  company: string;
  batchNo: string;
  expiryDate: string;
  /**
   * MED-697: the stock reading in the product's packaging levels, e.g.
   * "2 Cartons + 1 Pack". This card used to take a base-unit NUMBER and label
   * it "Cartons", which reported one carton of ten tablets as "10 Cartons" -
   * wrong twice over. The server counts; this renders.
   */
  available: string;
  imageUrl: string;
  status: StockStatus;
  // MED-706: `price` and `packagingType` are read from the most recently
  // listed priced batch, at THAT batch's own selling level (see the mapping in
  // all-products-order.tsx). A distributor breaking bulk sells by the pack, so
  // the level is the holder's, not the product's outermost layer. `available`
  // carries its own level already, rendered server-side (MED-697).
  price: string;
  packagingType: string;
  onOrder?: () => void;
};

export default function OrderByProductSearch({
  name,
  company,
  batchNo,
  expiryDate,
  available,
  imageUrl,
  status,
  price,
  packagingType,
  onOrder,
}: ProductCardProps) {
  const stock = productStatusStyles[status];

  return (
    <div className="">
      <article className="w-full max-w-[386px] rounded bg-white px-4 py-5 shadow-[0_6px_18px_rgba(15,23,42,0.16)]">
        <header className="mb-5 flex items-start justify-between gap-4 border-b border-neutral-100 pb-2.5">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold leading-5 text-neutral-800">
              {name}
            </h2>
            <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              {company}
            </p>
          </div>

          <span
            className={`inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold ${stock.badge}`}
          >
            <span className={`h-2 w-2 rounded-full ${stock.dot}`} />
            {stock.label}
          </span>
        </header>

        <section className="mb-6 grid grid-cols-[1fr_112px] gap-4">
          <div className="space-y-3">
            <InfoBlock label="Batch No:" value={batchNo} />
            <InfoBlock label="Expiry Date:" value={expiryDate} />
            {/* MED-697: `available` arrives already rendered by the server, at
                the level THIS holder sells at and summed per batch, so there is
                nothing to pluralise here. See the prop's own note. */}
            <InfoBlock label="Available:" value={available} />
          </div>

          <img
            src={imageUrl}
            alt={name}
            className="h-28 w-28 rounded object-cover"
          />
        </section>

        <section className="mb-7 flex items-center justify-center gap-2 rounded bg-violet-50/80 px-2 py-3">
          <BiTag className="h-4 w-4 text-neutral-900" strokeWidth={1.8} />
          <p className="text-xs text-neutral-500">Price:</p>
          <p className="text-sm font-bold text-neutral-950">
            {price} / {packagingType}
          </p>
        </section>

        <button
          type="button"
          disabled={stock.disabled}
          onClick={onOrder}
          className="h-11 w-full rounded bg-[#33145f] text-base font-medium text-white transition hover:bg-[#2a0f51] disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500"
        >
          Order
        </button>
      </article>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs leading-4 text-neutral-500">{label}</p>
      <p className="text-sm font-medium leading-4 text-neutral-800">{value}</p>
    </div>
  );
}

// Placeholder shown in place of OrderByProductSearch while a search/filter/
// sort/clear is in flight — same section layout (header, batch info + image,
// price line, order button) so the grid doesn't visibly reshuffle once real
// cards land.
export function OrderByProductSearchSkeleton() {
  return (
    <div className="">
      <article className="w-full max-w-[386px] rounded bg-white px-4 py-5 shadow-[0_6px_18px_rgba(15,23,42,0.16)]">
        <header className="mb-5 flex items-start justify-between gap-4 border-b border-neutral-100 pb-2.5">
          <div className="min-w-0 flex-1 space-y-1.5">
            <LoadingSkeleton height="1rem" width="70%" />
            <LoadingSkeleton height="0.625rem" width="40%" />
          </div>
          <LoadingSkeleton height="1.5rem" width="5rem" noPadding />
        </header>

        <section className="mb-6 grid grid-cols-[1fr_112px] gap-4">
          <div className="space-y-3">
            <LoadingSkeleton height="1.75rem" />
            <LoadingSkeleton height="1.75rem" />
            <LoadingSkeleton height="1.75rem" />
          </div>
          <LoadingSkeleton height="7rem" width="7rem" noPadding />
        </section>

        <div className="mb-7">
          <LoadingSkeleton height="3.5rem" />
        </div>

        <LoadingSkeleton height="2.75rem" />
      </article>
    </div>
  );
}
